import { useRef } from 'react';
import { useSelector } from 'react-redux';

import { AlertModal, AlertModalCloseButton, AlertModalTitle } from '@air/primitive-alert-modal';
import { Button } from '@air/primitive-button';

import { type BoardSearchLibrary, useSingleBoardSearchState } from '~/components/BoardSearch/shared/hooks';
import { BoardSelectAllAssetsAndFilesListItem } from '~/components/BoardSelect/components/BoardSelectAllAssetsAndFilesListItem';
import { PrivateBoardSelect, type PrivateBoardSelectProps } from '~/components/BoardSelect/PrivateBoardSelect';
import { UploadDisabledOnPageWarning } from '~/components/Modals/PreUploadModal/UploadDisabledOnPageWarning';
import { BOARDS_SELECT_EMPTY_OPTION } from '~/constants/BoardSearch';
import { ORGANIZE_YOUR_FILES_MODAL, TRANSACTION_MODAL_PRIMARY_CTA } from '~/constants/testIDs';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useIsViewingPeoplePageSelector } from '~/hooks/useIsViewingPeoplePageSelector';
import { useSavedFilterIdSelector } from '~/hooks/useSavedFilterIdSelector';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { useURLPersonIdSelector } from '~/hooks/useURLPersonIdSelector';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { type BoardsSelectOption } from '~/types/BoardSearch';
import { canCreateAsset } from '~/utils/permissions/assetPermissions';

export type PreUploadModalProps = {
  onCancel: () => void;
  initialBoard?: PrivateBoardSelectProps['initialBoard'];
  onUpload: (selectedBoard: BoardsSelectOption | null) => void;
  initialLibrary?: BoardSearchLibrary;
  title?: string;
  submitButtonText?: string;
};

export const PreUploadModal = ({
  initialBoard,
  onClose,
  onUpload,
  onCancel,
  title = 'Upload files to…',
  submitButtonText = 'Upload',
}: AirModalProps<PreUploadModalProps>) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const isViewingSavedFilter = useSavedFilterIdSelector();
  const isViewingLibraryPage = useURLLibraryIdSelector();
  const isViewingPersonPage = useURLPersonIdSelector();
  const isViewingPeoplePage = useIsViewingPeoplePageSelector();

  const board = useSelector(centralizedBoardSelector);
  const { selectedBoard, isBoardSelected, onSelectBoard, shouldShowSubBoards, isLibrarySelected, onSelectLibrary } =
    useSingleBoardSearchState({
      initialBoard,
      initialLibrary: board?.library,
    });

  const { getBoardPermissions } = useBoardPermissionsCache();

  const upload = () => {
    onUpload(selectedBoard);
    onClose();
  };

  const onDismissModal = () => {
    onSelectBoard(BOARDS_SELECT_EMPTY_OPTION);
    onCancel();
    onClose();
  };

  const isUploadDisabledOnPage =
    !!isViewingLibraryPage || !!isViewingSavedFilter || !!isViewingPersonPage || !!isViewingPeoplePage;

  return (
    <AlertModal
      className="flex flex-col gap-4"
      data-testid={ORGANIZE_YOUR_FILES_MODAL}
      isOpen
      leastDestructiveRef={cancelButtonRef}
      onDismiss={onDismissModal}
    >
      <header className="flex items-center justify-between">
        <AlertModalTitle>{title}</AlertModalTitle>
        <AlertModalCloseButton onClick={onDismissModal} />
      </header>
      <div className="flex flex-col gap-4">
        {!!isViewingSavedFilter && (
          <UploadDisabledOnPageWarning
            title="Unable to upload to a saved search"
            message="Saved searches are unable to receive direct content. Please upload to the All assets & files, or a board"
          />
        )}

        {!!isViewingLibraryPage && (
          <UploadDisabledOnPageWarning
            title="Unable to upload to a Library"
            message="Libraries are unable to receive direct content. Please upload to a board in the library."
          />
        )}

        {!!isViewingPeoplePage && (
          <UploadDisabledOnPageWarning
            title="Unable to upload to People"
            message="People page is unable to receive direct content. Please upload to the All assets & files, or a board."
          />
        )}

        {!!isViewingPersonPage && (
          <UploadDisabledOnPageWarning
            title="Unable to upload to Person"
            message="Person page is unable to receive direct content. Please upload to the All assets & files, or a board."
          />
        )}

        <PrivateBoardSelect
          getBoardDisabledMessage={() => `You don't have permission to upload to this board`}
          initialBoard={initialBoard}
          isBoardDisabled={(board) => !canCreateAsset(getBoardPermissions(board.id))}
          isBoardSelected={isBoardSelected}
          isLibrarySelected={isLibrarySelected}
          onSelectBoard={onSelectBoard}
          onSelectLibrary={onSelectLibrary}
          shouldShowSubBoards={shouldShowSubBoards}
        >
          <BoardSelectAllAssetsAndFilesListItem isSelected={!selectedBoard} onSelect={() => onSelectBoard(null)} />
        </PrivateBoardSelect>
      </div>
      <footer className="flex justify-end gap-3">
        <Button appearance="ghost" color="grey" onClick={onDismissModal} size="large" ref={cancelButtonRef}>
          Cancel
        </Button>
        <Button
          appearance="filled"
          color="blue"
          data-testid={TRANSACTION_MODAL_PRIMARY_CTA}
          disabled={isUploadDisabledOnPage && !selectedBoard}
          onClick={upload}
          size="large"
        >
          {submitButtonText}
        </Button>
      </footer>
    </AlertModal>
  );
};
